/* eslint-disable import/prefer-default-export */
import React from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { Box, Typography, Card, CardContent, Grid } from "@mui/material";

import { AttendanceSettingsPanel } from "../ResourceCentre/ModuleSettings/AttendanceSettings/AttendanceSettingsPanel";
import useIsUserRcAdmin from "../../hooks/useIsUserRcAdmin";

// reusable card styles
const cardBase = {
  borderRadius: 3,
  backdropFilter: "blur(10px)",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 20px 40px rgba(0,0,0,0.06)"
  }
};

// workflow card
function WorkflowCard({
  title,
  description,
  action,
  onClick
}: {
  title: string;
  description: string;
  action: string;
  onClick: () => void;
}): React.ReactNode {
  return (
    <Card
      onClick={onClick}
      sx={{
        ...cardBase,
        cursor: "pointer"
      }}
    >
      <CardContent>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {description}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mt: 2,
            fontWeight: 500,
            color: "primary.main"
          }}
        >
          {action} →
        </Typography>
      </CardContent>
    </Card>
  );
}

// main dashboard
export function AttendanceDashboard(): React.ReactNode {
  const dispatch = useDispatch();
  const isAdmin = useIsUserRcAdmin();

  return (
    <Box
      sx={{
        minHeight: "100%",
        p: 3
      }}
    >
      {/* Header */}
      <Box mb={3} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Attendance Control Center
          </Typography>

          <Typography variant="body1" color="text.secondary">
            Monitor team attendance, approve requests and resolve exceptions - all in one place.
          </Typography>
        </Box>

        {isAdmin && <AttendanceSettingsPanel />}
      </Box>

      {/* Workflows */}
      <Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <WorkflowCard
              title="Team Punches"
              description="Live punch-in / out feed for every employee."
              action="View team feed"
              onClick={() => dispatch(push("/attendance/punches"))}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <WorkflowCard
              title="Attendance Summary"
              description="Daily attendance summary for every employee."
              action="View team summary"
              onClick={() => dispatch(push("/reports/attendance/attendanceSummaryReport"))}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <WorkflowCard
              title="Leave Applications"
              description="Review and approve pending leave applications."
              action="Review requests"
              onClick={() => dispatch(push("/attendance/leaveApplications"))}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
